import { useMemo, useState } from "react";
import { PageError, PageHeader, PageLoader } from "@/components/common";
import { useDashboardReport } from "../api";
import {
  BestSellersCard,
  CategoryBreakdownCard,
  CustomerInsightsCard,
  DailyRevenueChart,
  DateRangeSelector,
  GiveawayCard,
  HourlyRevenueChart,
  KpiGrid,
  OrderTypeCard,
  PaymentMixCard,
  SalesProfitSummary,
  rangeFor,
  type DateRange,
} from "../components";

export function ReportsPage() {
  const [range, setRange] = useState<DateRange>(() => ({
    preset: "today",
    ...rangeFor("today"),
  }));
  const params = useMemo(
    () => ({ startDate: range.startDate, endDate: range.endDate }),
    [range.startDate, range.endDate],
  );

  const {
    data: report,
    isLoading,
    error,
    refetch,
  } = useDashboardReport(params);

  if (isLoading) {
    return <PageLoader />;
  }

  if (error || !report) {
    return <PageError message="Failed to load reports" onRetry={refetch} />;
  }

  return (
    <div className="container mx-auto space-y-6 p-4 sm:p-6">
      <PageHeader
        title="Dashboard"
        description="Sales, customers and what's selling — at a glance."
        actions={<DateRangeSelector value={range} onChange={setRange} />}
      />
      <SalesProfitSummary
        report={report}
        startDate={range.startDate}
        endDate={range.endDate}
      />
      <KpiGrid report={report} />
      <div className="grid gap-6 lg:grid-cols-2">
        <HourlyRevenueChart
          data={report.revenueByHour}
          peakHour={report.peakHour}
        />
        <DailyRevenueChart
          data={report.revenueByDay}
          busiestDay={report.busiestDay}
        />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <BestSellersCard items={report.topSellingProducts} />
        <CategoryBreakdownCard rows={report.categoryBreakdown} />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <CustomerInsightsCard data={report.customers} />
        <PaymentMixCard data={report.salesByPaymentMethod} />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <OrderTypeCard rows={report.orderTypeBreakdown} />
        <GiveawayCard
          data={report.giveaways}
          totalRevenue={report.totalRevenue}
        />
      </div>
    </div>
  );
}
